/**
 * Streaming Handlers — the turn lane.
 *
 * AGENT_START/END/ERROR, TEXT/THINKING deltas, AGENT_TOOL_USE, and the rAF batch
 * queue every high-frequency event coalesces into. What is left here is what is
 * welded to this file's two pieces of shared mutable state: the module-scoped
 * delta queue (`eventQueue` / `scheduleFlush` / `drainTextForTask`) and the
 * per-turn `erroredTurns` dedup set that AGENT_START, AGENT_END and
 * AGENT_ERROR all write.
 *
 * `registerStreamingHandlers` composes two sibling registrars for the regions
 * that touch neither — see `streamingToolHandlers.ts` and
 * `streamingSessionHandlers.ts`. The full set of subscriptions across all three,
 * and their teardown, is pinned by
 * `__tests__/streamingHandlers.registrationTable.test.ts`.
 */

import type { Socket } from "socket.io-client";
import type { TurnOutcome, ErrorBlock } from "@bash/shared";
import type { ClientPayloadOf } from "../socketService";
import {
  terminalReasonFor,
  TURN_OUTCOME_KINDS,
  describeBrowserAction,
  ENGINE_SIGN_IN_STATUS,
} from "@bash/shared";
import { EVENTS } from "@/constants/events";
import {
  captureFirstToken,
  noteResultPresented,
  turnEndedOk,
} from "../helpers/turnAnalytics";
import { useTaskStore, requestTaskSync } from "../taskStore";
import { getSelectedTaskId } from "../socketService";
import { noteAgentEvent } from "../turnWatchdog";
import {
  takePerceivedFirstToken,
  type FirstTokenKind,
} from "../helpers/perceivedTtft";
import { useBrowserStore } from "../browserStore";
import {
  DEFAULT_AGENT,
  type Message,
  type ContentBlock,
  type TaskState,
  type ToolUseBlock,
  type ToolSummary,
} from "../types";

import { removeMainPendingWhere } from "../pendingQueue";
import { isWaiting } from "@/lib/planTime";
import { hasActionableOutcomeVariant } from "@/components/blocks/ErrorBlock";
import {
  finalizeThinkingDurations,
  completeRunningTools,
} from "../helpers/contentBlocks";
import { registerStreamingToolHandlers } from "./streamingToolHandlers";
import { registerStreamingSessionHandlers } from "./streamingSessionHandlers";
import { sealedRecentlyFor, adoptMissedTurn } from "./missedStartAdopt";
import { clearAgentSurfaceOnEnd } from "./agentSurface";
import { applyAgentUsage, turnTokens } from "./tokenUsage";

// describeBrowserAction moved verbatim to @bash/shared/conversation (one
// narration voice for web + the extension side panel); re-exported so existing
// import sites (ContentBlockRenderer, tests) keep working unchanged.
export { describeBrowserAction };

// finalizeThinkingDurations / completeRunningTools moved verbatim to
// ../helpers/contentBlocks (pure, store-free, and needed by the tool registrar
// too — a leaf home is what keeps that split cycle-free). Re-exported so every
// existing import site keeps working unchanged.
export { finalizeThinkingDurations, completeRunningTools };

// ── Error-surfacing contract (PR-ERR-3) — gate predicates ──────────────────
// The invariant: no terminal turn renders empty/silent. Every failure surfaces a
// visible card; an empty completion gets a quiet "no output" affordance — EXCEPT a
// HITL-pause turn (deferred/awaiting), which an interaction card owns and must stay
// clean. ⭐ All error/no-output synthesis fires STRICTLY on a FAILURE signal, never
// on "this turn has no content" alone — that is the #1096/#1097 regression guard.

/**
 * Recoverable error outcome kinds. These surface as a TRANSIENT notice (the
 * spinner clears, the user can retry) — NOT a sticky terminal ErrorBlock card.
 * Mirrors the server's recoverable set (handleErrorOutcomeAtAgentEnd in
 * sandbox-relay.service.ts: rate_limited + network_error). `code` on an
 * AGENT_ERROR is the structured `outcome.kind`, so we match on it directly.
 */
const RECOVERABLE_ERROR_KINDS = new Set(["rate_limited", "network_error"]);

/**
 * The set of TERMINAL-failure TurnOutcome kinds — a structured `outcome.kind`
 * whose terminalReasonFor status is 'failed', minus the recoverable transients.
 * PR-ERR-2 emits AGENT_ERROR with `code === outcome.kind`, so an AGENT_ERROR
 * whose `code` is in this set is a genuine turn-ENDING failure that must render a
 * sticky ErrorBlock. A code NOT in this set (e.g. the legacy mid-turn
 * `error_during_execution`, or an undefined code) is informational — the agent
 * may emit a transient error and CONTINUE the same turn (the 2026-05-03
 * root-cause fix), so we must NOT freeze a card on it. Derived from the contract
 * classifier so it stays total as kinds are added.
 */
const TERMINAL_FAILURE_KINDS: ReadonlySet<string> = new Set(
  (TURN_OUTCOME_KINDS as readonly TurnOutcome["kind"][]).filter((kind) => {
    const status = terminalReasonFor({
      kind,
      raw: { subtype: "", errors: [] },
    } as unknown as TurnOutcome).status;
    return status === "failed" && !RECOVERABLE_ERROR_KINDS.has(kind);
  })
);

/**
 * TRUE iff the turn ended in a genuine FAILURE (an error reached the user). Keys
 * on the contract classifier, never on "no content". `terminalReasonFor().status
 * === 'failed'` is exactly the genuine-failure set; it EXCLUDES completed
 * (ok/truncated), refused (stopped), deferred (paused), and the four
 * status:'stopped' kinds (user_aborted/hook_blocked/max_turns/max_budget).
 */
function isFailedOutcome(outcome: TurnOutcome | undefined): boolean {
  return outcome ? terminalReasonFor(outcome).status === "failed" : false;
}

/**
 * ⭐ Is this terminal PROVISIONAL — a turn we lost contact with, rather than a
 * turn that ended?
 *
 * `detached` is the only such kind: the server could not reach the agent to stop
 * it, so the turn is very likely STILL RUNNING and its real terminal — success or
 * failure — is still to come. It is classified status:'failed' so its card
 * renders on a partial turn, but it must stay OUT of the per-turn error dedup in
 * BOTH directions:
 *
 *   · it must not be SUPPRESSED by a card the turn already rendered (a turn that
 *     failed and is then stopped still owes the user "Stop not confirmed"), and
 *   · it must not SUPPRESS the card the real terminal brings.
 *
 * Marking it swallowed a late `max_budget`'s Recharge button, a late
 * `model_error`'s Try again, and — on the outcome-only native path, which
 * persists no error block — the card entirely. That is the same disease the
 * detached terminal exists to remove (telling the user something untrue about
 * what happened to their turn), so it must not be reintroduced by the fix.
 */
function isProvisionalOutcome(outcome: TurnOutcome | undefined): boolean {
  return outcome?.kind === "detached";
}

/** The provisional card itself, wherever it already sits in a turn's content. */
function isProvisionalBlock(b: {
  type?: string;
  outcome?: { kind?: string };
}): boolean {
  return b.type === "error" && b.outcome?.kind === "detached";
}

/** An error card the turn's TERMINAL owns. A provisional notice is never one. */
function isTerminalErrorBlock(b: {
  type?: string;
  outcome?: { kind?: string };
}): boolean {
  return b.type === "error" && !isProvisionalBlock(b);
}

/**
 * Should an EMPTY terminal turn render an ErrorBlock carrying this outcome
 * (vs the generic "no output" affordance)?
 *
 * Route through the ErrorBlock variant resolver — if the outcome has an
 * actionable card (a meaningful title + CTA), render THAT so the affordance
 * isn't dropped. This covers BOTH genuine failures (model_error, billing_error,
 * …) AND the actionable `status:'stopped'` reasons that end empty:
 * `max_budget` (→ Recharge credits CTA), `max_turns` (→ Keep going),
 * `hook_blocked` (→ Continue), and `refused` with no text (→ Request was
 * declined). It is broader than `isFailedOutcome` for exactly those stopped-
 * but-actionable kinds.
 *
 * EXCLUDES `user_aborted`: a user Stop must show NO card and stay idle — we do
 * NOT change Stop behavior or re-touch the PR-ERR-2 lifecycle guarantee.
 * (HITL-pause exclusion is the caller's responsibility — see the call sites,
 * which compute this only when `!hitlPause`.)
 */
function shouldRenderOutcomeCardOnEmpty(
  outcome: TurnOutcome | undefined
): boolean {
  if (!outcome) return false;
  if (outcome.kind === "user_aborted") return false; // a Stop shows no card
  return hasActionableOutcomeVariant(outcome);
}

/**
 * ⭐ The HITL guard. A deferred/awaiting HITL-pause turn legitimately ends with no
 * streamed content (an interaction card renders separately). It must get NEITHER
 * a synthesized ErrorBlock NOR a "no output" affordance. We treat a turn as a
 * pause if ANY pause signal is present:
 *  - `paused: true` on AGENT_END (B-core's pause-ends-run flag), OR
 *  - the outcome classifies as `deferred` (terminalReasonFor status 'paused'), OR
 *  - the task already carries a pendingInteraction / is in the 'waiting' phase
 *    (interaction handlers set pendingInteraction + phase, the durable substrate
 *    proxy that AGENT_END preserves across turn-end), OR
 *  - the task is paused on the person's own used-up plan (engines T5,
 *    tasks.resume_at still ahead, isWaiting): the server holds it for the
 *    reset, which is a pause the person chose or will be offered, never a
 *    failure. A resumeAt already past is no pause (a missed clear expires).
 *  - the server says the next account carried the task on (several accounts,
 *    `planLimit: 'switched'`): the turn's plan limit is handled, so it is no
 *    failure and no "no output" either; the server's notice line says it.
 */
function isHitlPause(
  data: { paused?: boolean; outcome?: TurnOutcome; planLimit?: "switched" },
  task:
    | { pendingInteraction?: unknown; phase?: string; resumeAt?: string | null }
    | undefined
): boolean {
  if (data.paused === true) return true;
  if (data.planLimit === "switched") return true;
  if (data.outcome?.kind === "deferred") return true;
  if (task?.pendingInteraction) return true;
  if (task?.phase === "waiting") return true;
  if (isWaiting(task?.resumeAt)) return true;
  return false;
}

/**
 * Engines T5 — a turn opening ends a plan pause, on the web as on the server
 * (endPlanPauseOnTurnStart): the plan_limit card the server settled silently
 * leaves, and resumeAt clears here rather than waiting on Realtime. A late
 * Realtime null for that clear then finds nothing set, so it can never take
 * down a card minted after this turn; and a missed one can never leave a
 * finished turn reading paused (or hide its failure from isHitlPause).
 */
function endPlanPause(task: TaskState): void {
  removeMainPendingWhere(task, (pi) => pi.type === "plan_limit");
  task.resumeAt = null;
}

/**
 * Build a renderable ErrorBlock from a failed TurnOutcome. The structured
 * `outcome` rides through so ErrorBlock.getOutcomeVariant picks the rich,
 * kind-specific variant (model_unavailable / image_error / billing_error / …);
 * `error` text + `code` are the contract's user-facing label + kind for legacy
 * fallbacks. Never a string match — `code` is the discriminator.
 */
function synthesizeErrorBlock(outcome: TurnOutcome): ErrorBlock {
  const label =
    terminalReasonFor(outcome).label || "The agent ran into an issue";
  return {
    type: "error",
    error: label,
    code: outcome.kind,
    category: "generic",
    outcome,
  };
}

// ── Unified delta buffer — batch ALL high-frequency events per animation frame ──
// Reduces Zustand mutations from many-per-event to exactly 1 _mutateTasks() per frame.
// Text, thinking, tool input, tool progress, and status updates all coalesce here.

type BufferedEvent =
  | { kind: "text"; taskId: string; text: string }
  | { kind: "thinking"; taskId: string; thinking: string }
  | { kind: "thinkingStart"; taskId: string }
  | {
      kind: "toolProgress";
      taskId: string;
      toolUseId: string;
      elapsedSeconds?: number;
      line?: string;
    }
  | { kind: "status"; taskId: string; status: string | null };

const eventQueue: BufferedEvent[] = [];
let rafHandle: number | null = null;

// Legacy accessor — handleToolUse and handleAgentEnd need to drain buffered text
// for a specific task synchronously before adding tool blocks / finalizing.
function drainTextForTask(taskId: string): string | undefined {
  let text: string | undefined;
  for (let i = eventQueue.length - 1; i >= 0; i--) {
    const ev = eventQueue[i];
    if (ev.kind === "text" && ev.taskId === taskId) {
      text = text ? ev.text + text : ev.text;
      eventQueue.splice(i, 1);
    }
  }
  return text;
}

function scheduleFlush() {
  if (rafHandle !== null) return;
  rafHandle = requestAnimationFrame(() => {
    rafHandle = null;
    if (eventQueue.length === 0) return;

    // Snapshot and clear before mutating store
    const batch = eventQueue.splice(0);

    useTaskStore.getState()._mutateTasks((tasks) => {
      for (const ev of batch) {
        const t = tasks.get(ev.taskId);
        if (!t) continue;
        // The runner's status word is the TASK's, not the bubble's: a task can
        // wait on its plan sign-in with no bubble (a sync settled it), and the
        // word that ends the wait has to land as surely as the one that began it.
        if (ev.kind === "status") {
          t.sdkStatus = ev.status;
          continue;
        }
        if (!t.streamingMessage) continue;
        // A turn that streams is past its sign-in: if the word that ended the
        // wait was lost in a gap, the turn's first output ends it here.
        if (t.sdkStatus === ENGINE_SIGN_IN_STATUS) t.sdkStatus = null;
        const content = t.streamingMessage.content;

        switch (ev.kind) {
          case "text": {
            const lastBlock = content[content.length - 1];
            if (lastBlock?.type === "text") {
              (lastBlock as { type: "text"; text: string }).text += ev.text;
            } else {
              content.push({ type: "text", text: ev.text });
            }
            break;
          }
          case "thinkingStart": {
            // Native arm: a thinking/reasoning block OPENED. Mount the thinking
            // pill NOW (empty text, so ThinkingBlockComponent renders "Thinking…"
            // + its running timer) instead of waiting for the first delta —
            // Anthropic's summarized adaptive thinking lags the block start by
            // seconds. The first AGENT_THINKING_DELTA then appends into this same
            // block via the "most recent thinking block" path below. Guard against
            // a duplicate empty mount (event redelivery, or a second start with no
            // delta between) by reusing a trailing empty thinking block.
            const lastBlock = content[content.length - 1];
            if (
              lastBlock?.type === "thinking" &&
              !(lastBlock as { thinking: string }).thinking
            ) {
              break;
            }
            finalizeThinkingDurations(content);
            content.push({
              type: "thinking",
              thinking: "",
              _startedAt: Date.now(),
            } as ContentBlock);
            break;
          }
          case "thinking": {
            // Resolve grouping in this priority order:
            //   1. A thinking block already exists in this streaming message →
            //      append to the most recent one, even if non-thinking blocks
            //      sit between it and the array tail. This prevents the
            //      "two thinking blocks at the top" fragmentation when a
            //      tool_use is pushed synchronously between buffered batches.
            //   2. No prior thinking block → push fresh.
            let lastThinkingIdx = -1;
            for (let i = content.length - 1; i >= 0; i--) {
              if (content[i].type === "thinking") {
                lastThinkingIdx = i;
                break;
              }
            }
            if (lastThinkingIdx >= 0) {
              (
                content[lastThinkingIdx] as {
                  type: "thinking";
                  thinking: string;
                }
              ).thinking += ev.thinking;
            } else {
              content.push({
                type: "thinking",
                thinking: ev.thinking,
                _startedAt: Date.now(),
              } as ContentBlock);
            }
            break;
          }
          case "toolProgress": {
            for (const block of content) {
              if (
                block.type === "tool_use" &&
                (block as ToolUseBlock).toolUseId === ev.toolUseId
              ) {
                const tb = block as ToolUseBlock;
                if (ev.elapsedSeconds != null)
                  tb.elapsedSeconds = ev.elapsedSeconds;
                // K5-T7: Monitor streams per-line output mid-turn. Append the line
                // (cap to avoid unbounded growth on a chatty process).
                if (ev.line != null) {
                  const MAX_LINES = 500;
                  tb.progressLines = tb.progressLines
                    ? [...tb.progressLines, ev.line]
                    : [ev.line];
                  if (tb.progressLines.length > MAX_LINES) {
                    tb.progressLines = tb.progressLines.slice(-MAX_LINES);
                  }
                }
                break;
              }
            }
            break;
          }
        }
      }
    });
  });
}

/**
 * Stop the perceived-TTFT clock on the first agent streaming delta for a task
 * and emit `task:first_token` exactly once per user-send. Fast-paths out when
 * the clock isn't armed (already fired, or never sent) or the turn already
 * ended — so a stale post-AGENT_END delta never counts and no store write
 * happens on the common path.
 */
function notePerceivedFirstToken(taskId: string, kind: FirstTokenKind): void {
  const task = useTaskStore.getState().tasks.get(taskId);
  if (!task?.perceivedTtft || task.phase === "idle") return;
  let event: ReturnType<typeof takePerceivedFirstToken> = null;
  useTaskStore.getState()._mutateTasks((tasks) => {
    const t = tasks.get(taskId);
    if (t) event = takePerceivedFirstToken(t, kind);
  });
  const taken = event as ReturnType<typeof takePerceivedFirstToken>; // set in the callback, which control flow cannot see
  if (taken) captureFirstToken(taken, task);
}

export function registerStreamingHandlers(socket: Socket): () => void {
  const taskStore = useTaskStore;

  // ── Per-turn error-dedup (PR-ERR-3) ──
  // An error can be surfaced by TWO paths for the same turn — the AGENT_ERROR
  // handler (the live signal PR-ERR-2 emits) and the AGENT_END outcome synthesis
  // (belt-and-suspenders). Their wire order is racy. This set marks a task whose
  // CURRENT turn already rendered an error so the second path doesn't add a
  // duplicate card. Cleared on AGENT_START (a new turn).
  //
  // (PR-ERR-3 removed the old `blockTask`/`blockedTasks` empty-response hammer.
  // It committed a scary "couldn't generate a response" card on ANY empty turn —
  // including HITL pauses — and froze the task by rejecting all further events.
  // The server is now authoritative for terminal/error lifecycle (PR-ERR-2), and
  // an empty turn now gets a quiet no-output affordance, HITL-guarded.)
  const erroredTurns = new Set<string>();

  // ── Superseded turns, by their own ids (#4130 follow-up, defence in depth) ──
  // The runner stamps its turn id on AGENT_START and on the AGENT_END that closes that turn;
  // the server's reconcile END names the id the turn's START carried. An END naming a turn
  // this client has watched a later START REPLACE is a stray, and it is refused: on 2026-10-01
  // a reconcile END reached the web while a turn was running, idled the task and stamped
  // "Connection interrupted" over live work. Turn ids are UUIDs with no order, so an id this
  // client never saw is accepted: after a socket flap swallowed END(T1) and START(T2), the real
  // END(T2) is unknown here and must close the turn. An END naming no turn is accepted too.
  // Both maps clear on every socket connect: a reconnected client missed whatever happened
  // while it was away, so it refuses nothing on stale knowledge.
  const liveTurnIds = new Map<string, string>();
  const supersededTurnIds = new Map<string, Set<string>>();
  const SUPERSEDED_KEEP = 8; // per task; a stray END names a recent turn
  const supersede = (taskId: string, turnId: string) => {
    const seen = supersededTurnIds.get(taskId) ?? new Set<string>();
    seen.add(turnId);
    if (seen.size > SUPERSEDED_KEEP)
      seen.delete(seen.values().next().value as string);
    supersededTurnIds.set(taskId, seen);
  };
  const resetTurnIds = () => {
    liveTurnIds.clear();
    supersededTurnIds.clear();
  };

  const handleAgentStart = (
    data: ClientPayloadOf<typeof EVENTS.AGENT_START>
  ) => {
    const taskId = data.taskId || data.sessionId;
    const agentSlug = data.agentSlug || DEFAULT_AGENT;
    if (!taskId) return;

    // A START replaces the live turn. A replayed START of a turn already replaced revives nothing.
    if (!(data.turnId && supersededTurnIds.get(taskId)?.has(data.turnId))) {
      const previous = liveTurnIds.get(taskId);
      if (previous && previous !== data.turnId) supersede(taskId, previous);
      if (data.turnId) liveTurnIds.set(taskId, data.turnId);
      else liveTurnIds.delete(taskId);
    }

    // A new turn started — the previous turn's error-dedup mark is stale.
    erroredTurns.delete(taskId);

    const tasks = taskStore.getState().tasks;
    let currentTask = tasks.get(taskId);
    if (!currentTask) {
      // Unknown task that the user is LOOKING AT (page load mid-turn: deploy-skew
      // hard reload, deep link, second device) — the store was wiped but the page
      // is open on this task. Dropping the event blanked the task for the whole
      // turn (incident b6c10324). Seed a shell so the stream renders, and pull
      // server truth for the already-persisted history. Unknown NON-selected
      // tasks (background routines, other tabs' work) keep the ignore.
      if (getSelectedTaskId() !== taskId) {
        console.warn("[Socket] AGENT_START: Task not found:", taskId);
        return;
      }
      currentTask = taskStore.getState()._createTask(taskId, agentSlug);
      requestTaskSync(taskId);
    }

    // Idempotency guard: if already processing with an active streamingMessage,
    // this is either a duplicate AGENT_START (socket.io reconnect replay) OR the
    // FIRST real AGENT_START of a new task, whose optimistic send already seeded
    // phase='processing' + the placeholder (taskStore new-task path). The replay
    // must not re-open the turn, but the first-real case still owns the honest
    // turn clock: stamp agentStartedAt when it's missing (null on the optimistic
    // seed; already set on a genuine replay, so replays stay no-ops). Without
    // this, new-task first turns never show the footer timer at all.
    if (currentTask.phase === "processing" && currentTask.streamingMessage) {
      taskStore.getState()._mutateTasks((tasks) => {
        const t = tasks.get(taskId);
        if (t && t.agentStartedAt === null) t.agentStartedAt = Date.now();
        if (t) {
          t.resumingFrom = null;
          endPlanPause(t);
        }
      });
      return;
    }

    taskStore.getState()._mutateTasks((tasks) => {
      const task = tasks.get(taskId);
      if (!task) return;
      // Phase authority (P3.6 ratchet 1): SESSION_STATE_CHANGED{running} — NOT this
      // handler — flips phase→'processing'. The sandbox emits it on the SAME socket
      // immediately after EVERY AGENT_START (query-manager pairs each AGENT_START
      // emit with emitSessionStateChanged('running') on the next line), and the
      // server never originates AGENT_START, so the 'running' signal always follows.
      // AGENT_START keeps its OTHER turn-open work (clocks, streamingMessage, etc.).
      task.turnSummary = null;
      task.turnStartTime = Date.now();
      task.agentStartedAt = Date.now();
      // A turn is open again — our first-hand "I saw it end" knowledge is spent.
      task.turnSealedAt = null;
      task.turnEndedInterrupted = false;
      task.promptSuggestion = null;
      // The turn reopened: whatever it was resuming from, the work speaks now.
      task.resumingFrom = null;
      // A turn opening settles an ask the server never tells us about: a
      // connect it verified.
      removeMainPendingWhere(task, (pi) => pi.type === "auth");
      endPlanPause(task);

      // Multi-turn merge: if the last committed message is from the assistant
      // (no user message in between), pop it back into streamingMessage so this
      // turn's content appends to it — prevents separate bubbles per SDK turn.
      //
      // FAIL-SAFE OVERRIDE: when the sandbox flags this AGENT_START as a genuinely
      // NEW USER TURN (`data.newTurn === true`), ALWAYS open a fresh bubble — never
      // pop/merge the trailing assistant. This fixes the rapid-second-message
      // "message-mixing" bug where turn-2's output got appended into turn-1's bubble
      // because the user-message separator hadn't landed yet (staging/timing). Any
      // AGENT_START WITHOUT the flag keeps the EXACT legacy merge below.
      //
      // The flag itself is arm-neutral; correctness for the Claude arm rests on the
      // SANDBOX emitting `newTurn` ONLY for a genuine new user message and NEVER for a
      // silent resume ([SYSTEM_RESUME], shouldQuery:false) — see query-manager's
      // `!isSilentResume` gates on the pre-send re-arm + the cold startQuery call.
      // With that contract, every Claude resume sub-case lands here unflagged and
      // MERGES (byte-identical legacy multiple-assistant-blocks-per-prompt behavior);
      // only genuine new user turns open a fresh bubble.
      // RENDER_FROM_TRUTH — a live overlay from the PREVIOUS turn (held for
      // its sync round-trip, or an interrupt orphan) must never be clobbered
      // by the next turn's fresh bubble: phase flips idle at AGENT_END, so a
      // fast follow-up send reaches here before the round-trip completes.
      // Promote it into the committed list, inserted before the follow-up's
      // trailing optimistic user rows. A held row carries persisted identity
      // (serverId), so the eventual sync dedupes it in place; retention keeps
      // it safe until then (persisted-without-seq rows are never dropped).
      if (task.streamingMessage) {
        const { expectedServerId: heldServerId, ...promoted } =
          task.streamingMessage;
        // An ORPHAN promotion (interrupt end — no server id anywhere) is
        // marked so adoption can retire it when its DB flush lands; a
        // confirmed hold dedupes by identity.serverId instead.
        const isOrphan =
          !heldServerId && promoted.identity?.kind !== "persisted";
        const row = (
          isOrphan ? { ...promoted, awaitingEcho: true } : promoted
        ) as typeof task.streamingMessage;
        // Skip back over trailing user rows the server hasn't confirmed with a
        // seq yet — same-tab optimistic sends AND cross-tab broadcast echoes —
        // but only ones newer than this overlay's turn start: turn N's answer
        // row materializes server-side before those inserts, so seq truth puts
        // it first. Older seq-less rows (pre-flip storage) are real history.
        const ts = (d: Date | string) =>
          d instanceof Date ? d.getTime() : new Date(d).getTime();
        const overlayTs = ts(promoted.timestamp);
        let insertAt = task.messages.length;
        while (insertAt > 0) {
          const prev = task.messages[insertAt - 1];
          if (!(
            prev.role === "user" &&
            typeof prev.seq !== "number" &&
            ts(prev.timestamp) >= overlayTs
          ))
            break;
          insertAt--;
        }
        task.messages = [
          ...task.messages.slice(0, insertAt),
          row,
          ...task.messages.slice(insertAt),
        ];
        task.streamingMessage = null;
      }

      // RENDER_FROM_TRUTH — the fuse is the identity mutation this program
      // deletes: a committed (possibly persisted) row must never re-enter the
      // streaming slot. Every SDK turn opens a fresh overlay; committed rows
      // render one bubble per server row (today's cold-reload behavior).
      const clientId = `msg-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 7)}`;
      task.streamingMessage = {
        id: clientId,
        role: "assistant",
        content: [],
        timestamp: new Date(),
        agentSlug,
        identity: { kind: "optimistic", clientId },
      };
    });
  };

  const handleTextDelta = (
    data: ClientPayloadOf<typeof EVENTS.AGENT_TEXT_DELTA>
  ) => {
    const taskId = data.taskId || data.sessionId;
    if (!taskId) return;

    notePerceivedFirstToken(taskId, "text");

    const task = taskStore.getState().tasks.get(taskId);

    if (!task?.streamingMessage) {
      if (task) {
        // Task exists but no streamingMessage — AGENT_START was delayed or missed.
        // Only auto-create if task is NOT idle. If idle, AGENT_END already fired and
        // this TEXT_DELTA is stale (e.g. multi-turn race where turn 2 events leak
        // after turn 1 completed). Rendering it would create an orphaned zombie.
        // Seal-scoped drop (missedStartAdopt.ts has the full story): only a
        // first-hand, fresh turnSealedAt proves this delta is a stale replay.
        if (task.phase === "idle" && sealedRecentlyFor(task)) {
          console.warn(
            "[Socket] TEXT_DELTA after AGENT_END — dropping stale delta for:",
            taskId.slice(0, 8)
          );
          return;
        }
        console.warn(
          "[Socket] TEXT_DELTA before AGENT_START — auto-creating streamingMessage for:",
          taskId.slice(0, 8)
        );
        adoptMissedTurn(taskId, data.agentSlug || DEFAULT_AGENT, data.text);
      } else {
        // Task not in store at all — text will be lost (rare race condition)
        console.warn(
          "[Socket] TEXT_DELTA: task not found in store:",
          taskId.slice(0, 8)
        );
      }
      return;
    }

    // RENDER_FROM_TRUTH — a live overlay with phase idle is a finalized turn
    // awaiting its sync round-trip, not an open stream. A delta here is a
    // stale replay (legacy dropped it too: streamingMessage was null and the
    // phase===idle guard above fired). Buffering it would contaminate the
    // finalized turn.
    if (task.phase === "idle") {
      console.warn(
        "[Socket] TEXT_DELTA against a settled overlay — dropping stale delta for:",
        taskId.slice(0, 8)
      );
      return;
    }

    // Buffer delta — flush happens once per animation frame via rAF
    // Coalesce with previous text event for same task if possible
    const last = eventQueue[eventQueue.length - 1];
    if (last?.kind === "text" && last.taskId === taskId) {
      last.text += data.text;
    } else {
      eventQueue.push({ kind: "text", taskId, text: data.text });
    }
    scheduleFlush();
  };

  const handleThinkingDelta = (
    data: ClientPayloadOf<typeof EVENTS.AGENT_THINKING_DELTA>
  ) => {
    const taskId = data.taskId || data.sessionId;
    if (!taskId) return;

    // Missed-AGENT_START adoption, thinking lane (the flush drops thinking
    // for an overlay-less task — reasoning-first turns died whole). Same
    // seal discriminator as the text lane (missedStartAdopt.ts).
    const t0 = taskStore.getState().tasks.get(taskId);
    if (t0 && !t0.streamingMessage) {
      if (t0.phase === "idle" && sealedRecentlyFor(t0)) return;
      adoptMissedTurn(taskId, t0.agentSlug || DEFAULT_AGENT);
    }

    notePerceivedFirstToken(taskId, "thinking");

    // Coalesce with previous thinking event for same task if possible.
    const last = eventQueue[eventQueue.length - 1];
    if (last?.kind === "thinking" && last.taskId === taskId) {
      last.thinking += data.thinking;
    } else {
      eventQueue.push({ kind: "thinking", taskId, thinking: data.thinking });
    }
    scheduleFlush();
  };

  // AGENT_THINKING_START (native arm) — the model opened a thinking block. Mount
  // the thinking pill immediately, before any delta text. Routed through the same
  // queue as deltas so the mount lands in arrival order relative to them.
  const handleThinkingStart = (
    data: ClientPayloadOf<typeof EVENTS.AGENT_THINKING_START>
  ) => {
    const taskId = data.taskId || data.sessionId;
    if (!taskId) return;
    eventQueue.push({ kind: "thinkingStart", taskId });
    scheduleFlush();
  };

  // UNDECLARED by the wire contract (s2-07b finding): summary, display — read here, absent from AgentToolUsePayloadSchema.
  const handleToolUse = (
    data: ClientPayloadOf<typeof EVENTS.AGENT_TOOL_USE> & {
      summary?: ToolSummary;
      display?: ToolUseBlock["display"];
    }
  ) => {
    const taskId = data.taskId || data.sessionId;
    if (!taskId) return;

    // NOTE: auto-open-panel moved to handleToolUseStart — that handler
    // fires ONLY on live tool invocations, not on MESSAGE_SYNC replays.
    // Replays on reconnect were causing the "browser opens by itself even
    // with no agent activity" flap.

    // Drain buffered text + add tool block in a SINGLE Immer draft
    const bufferedText = drainTextForTask(taskId);

    taskStore.getState()._mutateTasks((tasks) => {
      const task = tasks.get(taskId);
      if (!task?.streamingMessage) return;
      const content = task.streamingMessage.content;

      // Flush buffered text first to preserve content order
      if (bufferedText) {
        const lastBlock = content[content.length - 1];
        if (lastBlock?.type === "text") {
          (lastBlock as { type: "text"; text: string }).text += bufferedText;
        } else {
          content.push({ type: "text", text: bufferedText });
        }
      }

      finalizeThinkingDurations(content);

      // Check if a preparing block already exists (from AGENT_TOOL_USE_START)
      const existingIdx = content.findIndex(
        (b) =>
          b.type === "tool_use" &&
          (b as ToolUseBlock).toolUseId === data.toolUseId
      );

      if (existingIdx >= 0) {
        // Upgrade preparing → running with full data
        const existing = content[existingIdx] as ToolUseBlock;
        existing.status = "running";
        existing.toolInput = data.toolInput;
        existing.summary = data.summary;
        existing.display = data.display;
      } else {
        // No preparing block — create directly as running (fast tool)
        content.push({
          type: "tool_use",
          toolUseId: data.toolUseId,
          toolName: data.toolName,
          toolInput: data.toolInput,
          status: "running",
          startTime: new Date(),
          summary: data.summary,
          display: data.display,
        } as ToolUseBlock);
      }
    });

    // Translate browser-driving tool calls into the chrome rail's plain-
    // language status. Two tool families flow through here:
    //   - mcp__browser-control__*  (Stagehand-style: navigate/act/observe/extract)
    //   - computer_*               (Anthropic Computer Use: click/type/scroll/...)
    // Both feed the same agentAction surface — keeping the rail as a single
    // source of truth for "what's happening right now."
    const action = describeBrowserAction(data.toolName, data.toolInput);
    if (action !== null) {
      const bs = useBrowserStore.getState();
      bs.setAgentControlling(true);
      if (bs.streamSessionId) {
        bs.notifyBrowserActivity();
      }
      useBrowserStore.setState({ agentAction: action });
      // Auto-pop the Computer panel on the Browser tab lives in
      // handleToolUseStart — that event only fires on live tool
      // invocations, not on MESSAGE_SYNC replays. Doing it here would
      // re-pop the panel on every reconnect (and on every subsequent
      // tool call). See the NOTE in handleToolUseStart below.
    }
  };

  // UNDECLARED by the wire contract (s2-07b finding): turnFooter, errorBlocks — read here, absent from AgentEndPayloadSchema.
  const handleAgentEnd = (
    data: ClientPayloadOf<typeof EVENTS.AGENT_END> & {
      turnFooter?: {
        durationMs: number;
        interrupted: boolean;
        interruptReason?: "user" | "disconnected" | "credits_exhausted" | "restart" | "restart_paused";
        word?: string;
        creditCost?: number;
      };
      errorBlocks?: Array<{ type: string; error: string; code?: string }>;
    }
  ) => {
    const taskId = data.taskId || data.sessionId;
    if (!taskId) return;

    if (data.turnId && supersededTurnIds.get(taskId)?.has(data.turnId)) {
      console.warn(
        "[Socket] AGENT_END for a turn a later START replaced, ignored:",
        taskId,
        data.turnId,
        "live:",
        liveTurnIds.get(taskId)
      );
      return;
    }

    // Drain buffered text synchronously before finalizing
    const remainingText = drainTextForTask(taskId);

    const task = taskStore.getState().tasks.get(taskId);
    if (!task) return;

    // Single Immer draft: flush text + finalize + commit
    taskStore.getState()._mutateTasks((tasks) => {
      const t = tasks.get(taskId);
      if (!t) return;

      // 1. Flush remaining buffered text
      if (remainingText && t.streamingMessage) {
        const content = t.streamingMessage.content;
        const lastBlock = content[content.length - 1];
        if (lastBlock?.type === "text") {
          (lastBlock as { type: "text"; text: string }).text += remainingText;
        } else {
          content.push({ type: "text", text: remainingText });
        }
      }

      // Stamp the SDK assistant-message uuid onto the committed message — it
      // remains the stable DB-side key later message events look up by.
      // AGENT_END is the last event before commit, so stamping here guarantees
      // it's present when the message enters task.messages.
      if (data.sdkMessageUuid && t.streamingMessage) {
        t.streamingMessage.sdkMessageUuid = data.sdkMessageUuid;
      }

      // Identity reconciliation — flip the optimistic streamingMessage to
      // persisted now that the server has assigned a DB row id. Future per-
      // message events (MESSAGE_CREDIT_UPDATE, thumbs, …)
      // match against any of clientId / serverId / sdkMessageUuid via
      // findMessageByAnyId, so they'll land on this message no matter which
      // flavor of id the sender picked.
      if (t.streamingMessage && data.messageId) {
        const existingIdentity = t.streamingMessage.identity;
        const clientId =
          existingIdentity?.kind === "optimistic"
            ? existingIdentity.clientId
            : existingIdentity?.kind === "persisted"
              ? existingIdentity.clientId
              : t.streamingMessage.id;
        t.streamingMessage.identity = {
          kind: "persisted",
          serverId: data.messageId,
          ...(clientId ? { clientId } : {}),
          ...(data.sdkMessageUuid
            ? { sdkMessageUuid: data.sdkMessageUuid }
            : {}),
        };
      }

      // 2. Finalize messages — complete running tools, append turn footer
      let finalMessages: Message[];

      // ⭐ HITL guard, computed ONCE for this turn. A deferred/awaiting pause
      // legitimately ends with no content; it must get neither an error block
      // nor a no-output affordance (the interaction card owns the turn).
      const hitlPause = isHitlPause(data, t);
      // Genuine-failure signal — keyed on the contract classifier, never on
      // "no content". A pause is never a failure (terminalReasonFor(deferred)
      // is 'paused', not 'failed'), so a pause never trips this. A real failure
      // gets a card even on a PARTIAL turn (text-then-failed).
      const turnFailed = !hitlPause && isFailedOutcome(data.outcome);
      // An EMPTY turn that is not a failure but carries an ACTIONABLE reason
      // (max_budget→Recharge, max_turns→Keep going, hook_blocked→Continue,
      // refused-no-text→declined) renders THAT card instead of the generic
      // no-output line. Excludes user_aborted (Stop → no card) and HITL.
      const emptyOutcomeCard =
        !hitlPause &&
        !turnFailed &&
        shouldRenderOutcomeCardOnEmpty(data.outcome);
      // ⭐ A PROVISIONAL terminal neither reads nor writes the per-turn error
      // dedup — see isProvisionalOutcome. Everything below routes its dedup
      // through these three, so the two card classes can never suppress each
      // other in either direction.
      // ⭐ A PROVISIONAL terminal — the stop button's `detached`, for a Stop we
      // could not deliver. TWO copies of its card exist by design, and never at
      // the same time:
      //   · this one, synthesized so it appears the instant the user presses
      //     Stop. Under RENDER_FROM_TRUTH it lives in the held overlay; with the
      //     flag OFF it is committed into the turn's row.
      //   · the SERVER's, folded into the same turn's event buffer
      //     (interaction-handlers → `agent:error_block`, pulled by TASK_SYNC_HINT).
      //     It is the copy with stream POSITION and DURABILITY — a client card
      //     held as a sibling row rendered above the output it annotates,
      //     permanently.
      // The client's is retired when the server's is ADOPTED (syncHandlers), not
      // when the overlay happens to settle in the same update — settlement is
      // timing, adoption is structural. Under the flag-OFF merge a MATCHED row
      // takes the server's content outright, so there the swap happens by itself.
      // Whichever copy is showing, the per-turn error dedup must not treat it as
      // the turn's TERMINAL card: the real terminal's failure still has to speak.
      const provisional = isProvisionalOutcome(data.outcome);
      const dedupHit = !provisional && erroredTurns.has(taskId);
      const markErrored = () => {
        if (!provisional) erroredTurns.add(taskId);
      };
      /** A card of the SAME class already in this content? The two never collide. */
      const sameClassCardIn = (blocks: Array<{ type: string }>) =>
        blocks.some(provisional ? isProvisionalBlock : isTerminalErrorBlock);
      // The errorBlocks path (Claude arm) keeps its broad dedup: a processor
      // error block must not double up with an existing one anywhere — but a
      // detached marker row is NOT such a block, so it must not stand in for the
      // terminal card the processor is delivering.
      const alreadyErrored = () =>
        dedupHit ||
        t.messages.some((m: any) => m.content?.some(isTerminalErrorBlock));
      /**
       * Branch A has no single content array to scope to, so scope to THIS TURN
       * the only way the committed list allows: the rows AFTER the last user
       * message. A thread-wide check reads turn 1's card as turn 2's and
       * suppresses turn 2 entirely — the very class the synthesis comment below
       * warns about ("a turn-1 error block still in `t.messages` must not
       * suppress this turn-2 synthesis"). The trailing assistant row is not the
       * scope either: branch A fires when the turn streamed NOTHING, so the last
       * assistant row usually belongs to a previous turn. What this must catch is
       * the one case it was written for — a SECOND detached END inside the same
       * turn, whose card the first one already committed after that user row.
       */
      const sameClassCommitted = () => {
        if (!provisional) return false;
        const lastUserIdx = t.messages
          .map((m: any) => m.role)
          .lastIndexOf("user");
        return t.messages
          .slice(lastUserIdx + 1)
          .some((m: any) => m.content?.some(isProvisionalBlock));
      };

      if (!t.streamingMessage) {
        // Defensive: streamingMessage is null when AGENT_END fires (rare — can
        // happen on reconnect when MESSAGE_SYNC committed an in-flight message
        // before AGENT_END landed, or on a resume of a swallowed-error turn that
        // never streamed anything — S4). Surface the terminal outcome either way.
        if (data.errorBlocks?.length && !alreadyErrored()) {
          // Processor sent explicit error blocks — render them (Claude arm path).
          // Prefer the server-stamped messageId (the server-side gates persist the
          // same block as a DB row under this id) so the live card and the durable
          // row share one identity — every merge path dedups instead of doubling.
          const errorClientId =
            data.messageId ??
            `error-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
          finalMessages = [
            ...t.messages,
            {
              id: errorClientId,
              role: "assistant" as const,
              content: data.errorBlocks.map((eb) => ({
                type: "error" as const,
                error: eb.error,
                code: eb.code,
                category: (eb as any).category || "generic",
                outcome: (eb as any).outcome,
              })) as any,
              timestamp: new Date(),
              agentSlug: task.agentSlug || DEFAULT_AGENT,
              identity: {
                kind: "optimistic" as const,
                clientId: errorClientId,
              },
            },
          ];
          markErrored();
        } else if (
          (turnFailed || emptyOutcomeCard) &&
          !dedupHit &&
          !sameClassCommitted()
        ) {
          // S4: the turn ended in a FAILURE, or empty with an actionable reason,
          // but carried no error block and never streamed — synthesize the
          // outcome card so the turn isn't a silent blank (or a generic
          // no-output that drops an actionable CTA). Attack-4: gate ONLY on the
          // per-turn `erroredTurns` set, NOT all of `t.messages` — a turn-1 error
          // block still in `t.messages` must not suppress this turn-2 synthesis.
          const errorClientId = `error-${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 7)}`;
          finalMessages = [
            ...t.messages,
            {
              id: errorClientId,
              role: "assistant" as const,
              content: [synthesizeErrorBlock(data.outcome!)],
              timestamp: new Date(),
              agentSlug: task.agentSlug || DEFAULT_AGENT,
              identity: {
                kind: "optimistic" as const,
                clientId: errorClientId,
              },
            },
          ];
          markErrored();
        } else {
          finalMessages = t.messages;
        }
      } else {
        const contentCopy = t.streamingMessage.content.map((block) =>
          block.type === "thinking" ? { ...block } : block
        );
        finalizeThinkingDurations(contentCopy);
        const finalContent = completeRunningTools(contentCopy);

        // Does the turn have any renderable content (before any footer)? An
        // EMPTY actionable outcome (max_budget/max_turns/hook_blocked/refused-no-
        // text) renders its card; a NON-empty one (text-then-stopped) lets the
        // footer chip carry the reason, so we DON'T add a redundant card.
        // A provisional card is OUR marker, not the turn's output: counting it as
        // content made an otherwise-EMPTY turn look productive and suppressed the
        // real terminal's actionable card (a late max_budget's Recharge CTA).
        const finalContentIsEmpty = !finalContent.some(
          (b) => b.type !== "turn_footer" && !isProvisionalBlock(b)
        );

        // Inject error blocks from AGENT_END (sent by processor via assistantBlocks)
        if (data.errorBlocks?.length) {
          for (const eb of data.errorBlocks) {
            // Dedup: skip if a TERMINAL card is already present. A detached
            // notice is not one — under RENDER_FROM_TRUTH the finalized turn is
            // HELD in the overlay rather than committed, so the real terminal
            // re-enters this branch with the detached card still in
            // `finalContent`; counting it here dropped the real failure's card.
            if (!finalContent.some(isTerminalErrorBlock)) {
              finalContent.push({
                type: "error" as const,
                error: eb.error,
                code: eb.code,
                category: (eb as any).category || "generic",
                outcome: (eb as any).outcome,
              } as any);
              markErrored();
            }
          }
        } else if (
          (turnFailed || (emptyOutcomeCard && finalContentIsEmpty)) &&
          !dedupHit &&
          !sameClassCardIn(finalContent)
        ) {
          // The turn ended in a FAILURE (card even on a partial turn), or ended
          // EMPTY with an actionable reason (card carries the CTA), but the
          // processor sent no error block (PR-ERR-2's outcome-only path) —
          // synthesize the outcome card so the turn isn't a silent blank / a
          // generic no-output that drops the CTA. NEVER fires for a pause
          // (both gates exclude hitlPause) or a user Stop (excluded by
          // shouldRenderOutcomeCardOnEmpty). Attack-4: dedup on the per-turn
          // `erroredTurns` set, not all of `t.messages`.
          finalContent.push(synthesizeErrorBlock(data.outcome!));
          markErrored();
        }

        if (
          data.turnFooter &&
          (data.turnFooter.durationMs > 0 || data.turnFooter.interrupted)
        ) {
          // A message carries at most one footer, and a completing turn's footer is
          // the truth: drop any stale turn_footer / no_output left by a prior
          // interrupted commit on this message before pushing the fresh footer.
          for (let i = finalContent.length - 1; i >= 0; i--) {
            if (
              finalContent[i].type === "turn_footer" ||
              finalContent[i].type === "no_output"
            ) {
              finalContent.splice(i, 1);
            }
          }
          finalContent.push({
            type: "turn_footer" as const,
            ...data.turnFooter,
            ...(data.stopReason &&
              data.stopReason !== "end_turn" && {
                stopReason: data.stopReason,
              }),
            // K6 P4: live terminalReason lands on the in-memory footer so the
            // chip + Continue CTA show without a reload, mirroring how the
            // server persists it onto the same block post-hoc. Native/openai
            // only; absent on Claude turns (footer renders byte-identical).
            ...(data.terminalReason && { terminalReason: data.terminalReason }),
            // Several accounts: the turn's own footer says the next account
            // carried it on, as the server's persisted row will on a reload.
            ...(data.planLimit === "switched" && {
              planLimit: "switched",
              ...(data.planLimitNetAt && {
                planLimitNetAt: data.planLimitNetAt,
              }),
            }),
          });
        }

        finalMessages = [
          ...t.messages,
          { ...t.streamingMessage, content: finalContent },
        ];
      }

      // 2b. No terminal turn renders empty/silent (PR-ERR-3). A committed
      // assistant turn that produced NO renderable content gets a visible
      // affordance instead of collapsing to a blank:
      //   - FAILURE / ACTIONABLE EMPTY OUTCOME → an ErrorBlock already
      //                synthesized above (failed outcome, errorBlocks, or an
      //                empty max_budget/max_turns/hook_blocked/refused-no-text).
      //                Nothing to do here.
      //   - ⭐ HITL PAUSE (deferred/awaiting) → NOTHING. The interaction card
      //                owns the turn; an error or no-output affordance here is
      //                exactly the #1096/#1097 regression. STRICTLY exempt.
      //   - USER STOP (user_aborted) → NOTHING — a Stop shows no card, stays
      //                idle (excluded via shouldRenderOutcomeCardOnEmpty).
      //   - BENIGN EMPTY (completed / no actionable reason, the model returned
      //                nothing) → a quiet `no_output` affordance — NOT the scary
      //                "couldn't generate a response" error.
      // `thinking` counts as content (S9): a reason-only turn is a valid
      // completion, not "couldn't generate a response".
      // A user Stop (user_aborted) ends a turn deliberately with nothing to say —
      // it shows no card and stays idle; the footer's "Stopped" chip carries it.
      // Never overlay a "no output" affordance on a Stop.
      const isUserStop = data.outcome?.kind === "user_aborted";
      // A disconnected interrupt means the link died mid-turn, not that the model
      // returned nothing — the turn may still be running upstream. Never fabricate a
      // "No output" affordance for it (the interrupted footer already tells the truth).
      const isDisconnectedInterrupt =
        data.turnFooter?.interrupted === true &&
        data.turnFooter?.interruptReason === "disconnected";
      const lastFinalMsg = finalMessages[finalMessages.length - 1];
      const isFreshlyCommittedTurn =
        t.streamingMessage != null || finalMessages !== t.messages;
      if (
        lastFinalMsg?.role === "assistant" &&
        isFreshlyCommittedTurn &&
        !hitlPause &&
        !turnFailed &&
        !emptyOutcomeCard &&
        !isUserStop &&
        !isDisconnectedInterrupt
      ) {
        const hasRenderableContent = lastFinalMsg.content.some(
          (b: { type: string }) =>
            b.type === "text" ||
            b.type === "tool_use" ||
            b.type === "thinking" ||
            b.type === "error" ||
            b.type === "checkpoint" ||
            b.type === "image" ||
            b.type === "question" ||
            b.type === "todo" ||
            b.type === "file_attachment" ||
            b.type === "compaction" ||
            b.type === "no_output" ||
            // A notice (a refused step, a plan heads-up) is a line the person
            // reads: a turn that says only that did not say nothing.
            b.type === "notice"
        );
        if (!hasRenderableContent) {
          // Append a no-output affordance to THIS committed turn (the
          // turn_footer, if any, already rode in). The model returned nothing —
          // benign, not an error.
          lastFinalMsg.content = [
            ...lastFinalMsg.content,
            { type: "no_output" as const },
          ];
        }
      }

      // 3. Update state. Duration is measured from agentStartedAt (the moment
      // the agent runner actually started this turn) so cold-boot time is
      // excluded. turnStartTime is still nulled here — task-list views read
      // it for their live elapsed display and must not see a stale value.
      const elapsed = t.agentStartedAt ? Date.now() - t.agentStartedAt : 0;
      t.turnSummary = { durationMs: elapsed, tokens: turnTokens(t.tokenUsage) };
      t.turnStartTime = null;
      t.agentStartedAt = null;
      // Phase authority (P3.6 ratchet 1): KEPT — NOT yet redundant with
      // SESSION_STATE_CHANGED{idle}. That signal covers only the sandbox
      // query-manager turn-ends (_emitTurnEnd + the stranded-continue /
      // teammate-refuse / prewarm-fail finalizers all emit it before their
      // AGENT_END). FIVE AGENT_END paths emit NO paired idle, so this write is the
      // sole spinner-clear there:
      //   • sandbox graceful shutdown       (apps/sandbox/src/index.ts, reason:'shutdown')
      //   • server model_unavailable refuse (message-handlers.ts)
      //   • server image-unsupported refuse (message-handlers.ts)
      //   • server interrupt-kill fallback  (interaction-handlers.ts, WS dead)
      //   • server reconcile-sweep AGENT_END (sandbox-relay pushReconciledTaskEnds — standby/upgrade)
      // Removing this write is a LATER ratchet, gated on those paths also emitting
      // SESSION_STATE_CHANGED{idle} (a server + shutdown change, out of this file's scope).
      t.phase = "idle";
      // First-hand terminal knowledge: this client just watched the runner's own
      // AGENT_END seal the turn. A MESSAGE_SYNC that still says 'processing' is
      // reading a row we already know is behind — it must not re-arm the loader
      // (founder 08-20, task 54510a8b). Cleared when a turn re-opens.
      t.turnSealedAt = Date.now();
      // …and WHAT we saw: a cut-short terminal, from the runner's interrupted footer
      // or the server's own stop. The helper lane's ending line reads it when no
      // report row reached this client (C1), so a killed run never reads "finished".
      t.turnEndedInterrupted =
        data.turnFooter?.interrupted === true ||
        data.stopReason === "user_interrupt" ||
        data.stopReason === "credits_exhausted";
      // A cut-short task resumes from nothing: the Stop withdrew the ask it was
      // waiting on, so "picking up your answer" would read over a stopped task
      // until a turn that never comes (staging 10-02, task f517766b).
      if (t.turnEndedInterrupted) t.resumingFrom = null;
      // …and, when the next account took this turn's plan limit over, the
      // exact resume_at its safety net armed: that one resumeAt is never a
      // wait. A value, never a mode: it can hide no other resumeAt, so it
      // needs no clearing.
      if (data.planLimit === "switched" && data.planLimitNetAt)
        t.switchNetAt = data.planLimitNetAt;
      // RENDER_FROM_TRUTH — the finalized turn HOLDS in the overlay instead
      // of committing: the committed list only ever gains rows from
      // MESSAGE_SYNC. With a messageId (every normal turn end, and — since the
      // Stop fix, founder 08-20 — the INTERRUPT flush too, which now reserves
      // its row id before writing) the overlay is HELD: settleOverlay retires it
      // when its row arrives. WITHOUT one (a disconnect/reconcile end, or an
      // older server bundle) it holds as an ORPHAN: committing it minted a
      // client-only row that duplicated the DB-flushed row on the next sync
      // (PR #1676 review, blocker 2); the orphan rule retires it when the
      // flushed row lands.
      if (t.streamingMessage != null) {
        const finalized = finalMessages[finalMessages.length - 1];
        t.streamingMessage = data.messageId
          ? { ...finalized, expectedServerId: data.messageId }
          : { ...finalized };
      } else {
        t.messages = finalMessages;
        t.streamingMessage = null;
      }
      t.sdkStatus = null;
      t.retryState = null;
      // P2.5b OS-hands signal integrity: the turn is over, so clear the ephemeral
      // hands-active line. Safe against a cross-turn under-claim because the sandbox
      // flushes the dedup baseline to `inactive` at this same turn boundary
      // (flushOsActivityAtTurnEnd) — a follow-up turn's hands re-emit `active` from
      // an inactive baseline rather than dedup-dropping into silence, and a reload
      // mid-errand recovers the live state via the reconnect re-emit.
      t.handsActive = undefined;
      // Substrate-v2: pendingInteraction is the durable substrate row's
      // proxy in FE state. With fire-return, the SDK turn ends immediately
      // after firing — clearing pendingInteraction here would erase the
      // card before the user could interact with it. Preserve any pending
      // interaction across turn-end; it's cleared by interactionHandlers
      // when actually resolved (or by Realtime handler on substrate state
      // change).
      t.pendingInteraction = task.pendingInteraction ?? null;
    });

    // RENDER_FROM_TRUTH — the commit signal. No live event carries the
    // committed row + seq (MESSAGE_CREATED has no emitter; MESSAGE_COMPLETE is
    // sandbox→server only), so request a sync: the row arrives with its seq,
    // the adoption places it, and settleOverlay retires the overlay (held by
    // id, or orphan via the new-assistant-row rule on interrupt flushes).
    socket.emit(EVENTS.MESSAGE_SYNC_REQUEST, { taskId });

    noteResultPresented(
      taskId,
      !isHitlPause(data, task) && turnEndedOk(data),
      taskStore.getState().selectedTaskId,
      data.messageId
    );

    // Clear browser controlling state when turn ends
    useBrowserStore.getState().setAgentControlling(false);
    useBrowserStore.setState({ agentAction: null });

    // Desktop presence: the turn ended, nothing is live (flag-gated inside).
    clearAgentSurfaceOnEnd(taskId);

    // Fetch real task cost from API (ledger is source of truth)
    // Runs async after state update — doesn't block the UI
    import("../creditStore")
      .then(({ useCreditStore }) => {
        useCreditStore
          .getState()
          .fetchTaskBreakdown(taskId)
          .then((breakdown) => {
            if (breakdown) {
              useCreditStore.setState((state) => {
                state.taskCosts.set(taskId, breakdown.totalCost);
              });
            }
          });
      })
      .catch(() => {
        /* silent */
      });

    // Phase 6 Branch — branched tasks have a known live-event gap for the
    // first few turns post-fork: the in-memory turn_footer lands without
    // `word` or `creditCost`, even though both persist correctly in the DB.
    // Triggering a MESSAGE_SYNC shortly after commit rehydrates the footer
    // from DB so the hover chip and completion word are correct without
    // requiring a page reload.
    //
    // Delay: ~1.5s to let MESSAGE_CREDIT_UPDATE (which arrives ~1s after
    // AGENT_END per PR #107's credit-ledger serializer) flush to DB before
    // we re-fetch.
    const branchedTask = taskStore.getState().tasks.get(taskId);
    if (branchedTask?.parentTaskId) {
      setTimeout(() => {
        socket.emit(EVENTS.MESSAGE_SYNC_REQUEST, { taskId });
      }, 1500);
    }
  };

  /**
   * AGENT_ERROR handler.
   *
   * PR-ERR-2 emits AGENT_ERROR whenever a turn ends in a classified ERROR outcome
   * (model_error / model_unavailable / billing_error / image_error / rate_limited / …).
   * `code` is the structured `outcome.kind`. PR-ERR-3 RENDERS it (it was
   * console-only): a terminal failure commits a visible ErrorBlock; a recoverable
   * error (rate_limited / network_error) is a TRANSIENT notice — the spinner
   * clears and the user can retry, but no sticky terminal card is committed.
   *
   * AGENT_END remains the finalization source of truth. When a streamingMessage is
   * active we append the error INTO it so AGENT_END finalizes a single message;
   * otherwise we commit a standalone error message. Either way we mark the turn
   * `erroredTurns` so AGENT_END's outcome-synthesis path doesn't double-render.
   *
   * ⭐ HITL guard: never commit an error over a paused/awaiting turn. A
   * pendingInteraction (or 'waiting' phase) means an interaction card owns the
   * turn — a transient AGENT_ERROR there must not clobber it. (PR-ERR-2 gates its
   * emit on the classifier, which excludes deferred; this is defense-in-depth.)
   *
   * Session-not-found stays a phase-only recovery — when the SDK session is gone,
   * AGENT_END won't fire to terminate the turn, so we clear `phase` to avoid a
   * stuck spinner.
   */
  // UNDECLARED by the wire contract (s2-07b finding): code, category — read here, absent from AgentErrorPayloadSchema.
  const handleAgentError = (
    data: ClientPayloadOf<typeof EVENTS.AGENT_ERROR> & {
      code?: string;
      category?: string;
    }
  ) => {
    const taskId = data.taskId || data.sessionId;
    console.error(
      "[Socket] AGENT_ERROR:",
      data.error,
      "(code:",
      data.code || "none",
      ")"
    );
    if (!taskId) return;

    const task = taskStore.getState().tasks.get(taskId);

    // Session-not-found: phase-only recovery (no card — there's nothing to retry
    // in this dead session; a new message starts a fresh one).
    if (data.error?.toLowerCase().includes("session not found")) {
      taskStore.getState()._mutateTasks((tasks) => {
        const t = tasks.get(taskId);
        // The dead session won't emit AGENT_END, so stop the turn clock here.
        if (t && (t.phase === "processing" || t.phase === "waiting")) {
          t.phase = "idle";
          t.agentStartedAt = null;
        }
      });
      return;
    }

    // ⭐ HITL guard — an interaction card owns a paused/awaiting turn. Never
    // commit/flip it on a transient error signal.
    if (task?.pendingInteraction || task?.phase === "waiting") return;

    // Recoverable (rate_limited / network_error) → transient notice only: clear
    // the spinner so the UI isn't stuck, but don't commit a sticky error card.
    // AGENT_END (which follows) lands the task idle / surfaces a retry chip.
    const recoverable =
      data.code != null && RECOVERABLE_ERROR_KINDS.has(data.code);
    if (recoverable) {
      taskStore.getState()._mutateTasks((tasks) => {
        const t = tasks.get(taskId);
        if (t && t.phase === "processing") {
          t.phase = "idle";
          t.agentStartedAt = null;
        }
      });
      return;
    }

    // Only a structured TERMINAL-failure code (= a failed `outcome.kind` PR-ERR-2
    // emits at turn end) renders a sticky card. A legacy/generic mid-turn code
    // (`error_during_execution`, undefined, …) is INFORMATIONAL — the agent may
    // emit a transient error and CONTINUE the same turn; freezing a card here is
    // the 2026-05-03 regression. Leave the turn untouched; AGENT_END finalizes it.
    if (data.code == null || !TERMINAL_FAILURE_KINDS.has(data.code)) return;

    // Terminal failure → render a visible ErrorBlock. Dedup against AGENT_END.
    if (erroredTurns.has(taskId)) return;
    const errorBlock: ErrorBlock = {
      type: "error",
      error: data.error || "The agent ran into an issue",
      code: data.code,
      category: data.category || "generic",
      // `code` is the structured outcome.kind, so reconstruct a minimal outcome
      // to drive ErrorBlock.getOutcomeVariant's rich, kind-specific card. The
      // `message` field is read (with a fallback) by the image/model variants.
      ...(data.code
        ? {
            outcome: {
              kind: data.code,
              message: data.error,
            } as unknown as TurnOutcome,
          }
        : {}),
    };
    taskStore.getState()._mutateTasks((tasks) => {
      const t = tasks.get(taskId);
      if (!t) return;
      if (t.streamingMessage) {
        // Append into the in-flight message; AGENT_END finalizes one message and
        // its errorBlocks-dedup skips re-adding (it checks for an existing error).
        if (!t.streamingMessage.content.some((b) => b.type === "error")) {
          t.streamingMessage.content.push(errorBlock);
        }
      } else {
        // No in-flight message — commit a standalone error message.
        const errorClientId = `error-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2, 7)}`;
        t.messages = [
          ...t.messages,
          {
            id: errorClientId,
            role: "assistant" as const,
            content: [errorBlock],
            timestamp: new Date(),
            agentSlug: t.agentSlug || DEFAULT_AGENT,
            identity: { kind: "optimistic", clientId: errorClientId },
          },
        ];
        if (t.phase === "processing") {
          t.phase = "idle";
          t.agentStartedAt = null;
        }
      }
    });
    erroredTurns.add(taskId);
  };

  // AGENT_TODO is consumed by contextPanelHandlers (which updates
  // taskStore.tasks[id].todos). The TodosBulgePeek above the InputStation
  // reads that state directly — no inline injection into streamingMessage
  // needed any more.

  const handleAgentUsage = (
    data: ClientPayloadOf<typeof EVENTS.AGENT_USAGE>
  ) => {
    const taskId = data.taskId || data.sessionId;
    if (!taskId) return;
    taskStore.getState()._mutateTasks((tasks) => {
      const task = tasks.get(taskId);
      if (!task) return;
      applyAgentUsage(task.tokenUsage, data.usage);
    });
  };

  // UNDECLARED by the wire contract (s2-07e finding): sessionId, elapsedSeconds — read here, absent from AgentToolProgressPayloadSchema (the Monitor tool, its only emitter, sends neither).
  const handleToolProgress = (
    data: ClientPayloadOf<typeof EVENTS.AGENT_TOOL_PROGRESS> & {
      sessionId?: string;
      elapsedSeconds?: number;
    }
  ) => {
    const taskId = data.taskId || data.sessionId;
    if (!taskId) return;

    // K5-T7: a per-line progress event (Monitor) carries `line`. Each line is
    // distinct output, so DON'T coalesce — push every line in arrival order so
    // the tool block shows the live stream. (An elapsedSeconds-only heartbeat
    // still coalesces below: only the latest timer value matters.)
    if (data.line != null) {
      eventQueue.push({
        kind: "toolProgress",
        taskId,
        toolUseId: data.toolUseId,
        line: data.line,
      });
      scheduleFlush();
      return;
    }

    // Overwrite previous elapsed-only progress event for same tool (only latest matters)
    const existing = eventQueue.find(
      (ev) =>
        ev.kind === "toolProgress" &&
        ev.taskId === taskId &&
        ev.toolUseId === data.toolUseId &&
        ev.line == null
    );
    if (existing && existing.kind === "toolProgress") {
      existing.elapsedSeconds = data.elapsedSeconds;
    } else {
      eventQueue.push({
        kind: "toolProgress",
        taskId,
        toolUseId: data.toolUseId,
        elapsedSeconds: data.elapsedSeconds,
      });
    }
    scheduleFlush();
  };

  // UNDECLARED by the wire contract (s2-07e finding): sessionId — read here (the taskId fallback), absent from AgentStatusPayloadSchema.
  const handleAgentStatus = (
    data: ClientPayloadOf<typeof EVENTS.AGENT_STATUS> & { sessionId?: string }
  ) => {
    const taskId = data.taskId || data.sessionId;
    if (!taskId) return;

    // Overwrite previous status event for same task (only latest matters)
    const existing = eventQueue.find(
      (ev) => ev.kind === "status" && ev.taskId === taskId
    );
    if (existing && existing.kind === "status") {
      existing.status = data.status;
    } else {
      eventQueue.push({ kind: "status", taskId, status: data.status });
    }
    scheduleFlush();
  };

  // Connection-truth: one seam stamps the turn watchdog's silence clock for
  // every agent-stream event — onAny so the stamp can't drift from the
  // handler list (and off/on symmetry stays trivial).
  const AGENT_CLOCK_EVENTS = new Set<string>([
    EVENTS.AGENT_START,
    EVENTS.AGENT_TEXT_DELTA,
    EVENTS.AGENT_THINKING_DELTA,
    EVENTS.AGENT_THINKING_START,
    EVENTS.AGENT_TOOL_USE,
    EVENTS.AGENT_TOOL_RESULT,
    EVENTS.AGENT_TOOL_USE_START,
    EVENTS.AGENT_TOOL_PROGRESS,
    EVENTS.AGENT_STATUS,
    EVENTS.AGENT_END,
    EVENTS.AGENT_ERROR,
    EVENTS.SESSION_STATE_CHANGED,
  ]);
  const stampAgentClock = (event: string, ...args: unknown[]) => {
    if (!AGENT_CLOCK_EVENTS.has(event)) return;
    const d = args[0] as Record<string, unknown> | undefined;
    const id =
      (d?.taskId as string | undefined) || (d?.sessionId as string | undefined);
    if (id) noteAgentEvent(id);
  };
  // Optional-called: unit-test socket fakes are bare EventEmitters without
  // onAny; the real socket.io Socket always has it.
  socket.onAny?.(stampAgentClock);

  socket.on("connect", resetTurnIds);
  socket.on(EVENTS.AGENT_START, handleAgentStart);
  socket.on(EVENTS.AGENT_TEXT_DELTA, handleTextDelta);
  socket.on(EVENTS.AGENT_THINKING_DELTA, handleThinkingDelta);
  socket.on(EVENTS.AGENT_THINKING_START, handleThinkingStart);
  socket.on(EVENTS.AGENT_TOOL_USE, handleToolUse);
  socket.on(EVENTS.AGENT_END, handleAgentEnd);
  socket.on(EVENTS.AGENT_ERROR, handleAgentError);
  socket.on(EVENTS.AGENT_USAGE, handleAgentUsage);
  const offToolHandlers = registerStreamingToolHandlers(socket);
  socket.on(EVENTS.AGENT_TOOL_PROGRESS, handleToolProgress);
  socket.on(EVENTS.AGENT_STATUS, handleAgentStatus);
  const offSessionHandlers = registerStreamingSessionHandlers(socket);

  return () => {
    socket.offAny?.(stampAgentClock);
    socket.off("connect", resetTurnIds);
    socket.off(EVENTS.AGENT_START, handleAgentStart);
    socket.off(EVENTS.AGENT_TEXT_DELTA, handleTextDelta);
    socket.off(EVENTS.AGENT_THINKING_DELTA, handleThinkingDelta);
    socket.off(EVENTS.AGENT_THINKING_START, handleThinkingStart);
    socket.off(EVENTS.AGENT_TOOL_USE, handleToolUse);
    socket.off(EVENTS.AGENT_END, handleAgentEnd);
    socket.off(EVENTS.AGENT_ERROR, handleAgentError);
    socket.off(EVENTS.AGENT_USAGE, handleAgentUsage);
    offToolHandlers();
    socket.off(EVENTS.AGENT_TOOL_PROGRESS, handleToolProgress);
    socket.off(EVENTS.AGENT_STATUS, handleAgentStatus);
    offSessionHandlers();
    // Clean up event queue
    eventQueue.length = 0;
    if (rafHandle !== null) {
      cancelAnimationFrame(rafHandle);
      rafHandle = null;
    }
  };
}
